import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL ;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Members API functions
export const membersApi = {
  // Get all members for public display
  getPublicMembers: async (params = {}) => {
    try {
      const response = await axios.get('/api/members/public', { params });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching public members:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all members (admin)
  getAllMembers: async () => {
    try {
      const response = await axios.get('/api/members');
      // Backend returns { success: true, data: members }
      // console.log('API response data:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching all members:', error);
      return { success: false, error: error.message };
    }
  },

  // Get single member
  getMember: async (id) => {
    try {
      const response = await axios.get(`/api/members/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching member:', error);
      throw error;
    }
  },

  // Create new member
  createMember: async (memberData, profileImage = null) => {
    try {
      const formData = new FormData();

      // Add member data
      Object.keys(memberData).forEach(key => {
        if (memberData[key] !== null && memberData[key] !== undefined) {
          if (typeof memberData[key] === 'object' && !(memberData[key] instanceof File)) {
            formData.append(key, JSON.stringify(memberData[key]));
          } else {
            formData.append(key, memberData[key]);
          }
        }
      });

      // Add profile image if provided
      if (profileImage) {
        formData.append('profileImage', profileImage);
      }

      // Don't set Content-Type manually — axios sets it automatically for
      // FormData with the correct multipart boundary.
      const response = await axios.post('/api/members', formData);

      return response.data;
    } catch (error) {
      console.error('Error creating member:', error);
      throw error;
    }
  },

  // Update member
  updateMember: async (id, memberData, profileImage = null) => {
    try {
      const formData = new FormData();

      // Add member data
      Object.keys(memberData).forEach(key => {
        if (memberData[key] !== null && memberData[key] !== undefined) {
          if (typeof memberData[key] === 'object') {
            formData.append(key, JSON.stringify(memberData[key]));
          } else {
            formData.append(key, memberData[key]);
          }
        }
      });

      // Add profile image if provided
      if (profileImage) {
        formData.append('profileImage', profileImage);
      }

      // Don't set Content-Type manually — axios sets it automatically for
      // FormData with the correct multipart boundary.
      const response = await axios.put(`/api/members/${id}`, formData);

      return response.data;
    } catch (error) {
      console.error('Error updating member:', error);
      throw error;
    }
  },

  // Delete member
  deleteMember: async (id) => {
    try {
      const response = await axios.delete(`/api/members/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting member:', error);
      throw error;
    }
  },

  // Update member priority
  updateMemberPriority: async (id, priority) => {
    try {
      const response = await axios.patch(`/members/${id}/priority`, { priority });
      return response.data;
    } catch (error) {
      console.error('Error updating member priority:', error);
      throw error;
    }
  },

  // Get departments list
  getDepartments: async () => {
    try {
      const response = await axios.get('/members/meta/departments');
      return response.data;
    } catch (error) {
      console.error('Error fetching departments:', error);
      throw error;
    }
  },

  // Bulk update priorities (for drag & drop)
  updateMemberPriorities: async (priorityUpdates) => {
    try {
      const promises = priorityUpdates.map(({ id, priority }) =>
        axios.patch(`/api/members/${id}/priority`, { priority })
      );

      const responses = await Promise.all(promises);
      return responses.map(response => response.data);
    } catch (error) {
      console.error('Error updating member priorities:', error);
      throw error;
    }
  }
};

export default membersApi;